import { modelOptions, mongoose, post, prop, Severity } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, mongooseValidatorFromZod, validate } from "../../../zod-decorator/index.js";
import { translationParamsZodValidator } from "../../commonZodValidators/translationParams.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type {
    WarningProblemEntryFieldsWithDefaults,
    WarningProblemEntrySchemaTypes,
} from "../../types/warningProblemEntry.d.js";
import { trackMixed } from "../../utils/mixedSchemaSavingUtils.js";
import type { DtoValidatorTypes } from "../../validatableClass.js";
import { declaredKeyValidator } from "./declaredKeyValidator.js";

const SCHEMA_KEY = "WarningProblemEntry" as const;

/**
 * One entry of a warning's `warningProblems` list: a "potential cause" identified by
 * `problemKey` with a catalog string under `<warningType>.problems.<problemKey>`.
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<WarningProblemEntry>>(...addSchemaKeyHook(SCHEMA_KEY))
export class WarningProblemEntry extends SchemaBase<WarningProblemEntry, WarningProblemEntryFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /**
     * Catalog identifier for this problem. Must be declared in the owning warning
     * type's `possibleProblemKeys`; standalone entries (no owner document) skip that
     * check, as does an unknown warningType — its own validator is the sole reporter.
     */
    @validate({ zodValidator: z.string().min(1), responseOnly: true })
    @prop({ type: () => String, required: true, validate: declaredKeyValidator("possibleProblemKeys") })
    public problemKey!: string;

    /**
     * Interpolation values for the problem's translation template, subject to the
     * same i18next reserved-key rules as `warningParams`.
     */
    @validate({ zodValidator: translationParamsZodValidator().optional(), responseOnly: true })
    @trackMixed
    @prop({
        type: () => mongoose.Schema.Types.Mixed,
        required: false,
        allowMixed: Severity.ALLOW,
        validate: mongooseValidatorFromZod(translationParamsZodValidator()),
    })
    public problemParams?: Record<string, string | number>;

    /**
     * Default-locale display text persisted at save time so the UI has a readable
     * fallback if the catalog key is later removed or renamed.
     */
    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public defaultDisplayText?: string;

    static getters = {} as const satisfies Record<string, (self: WarningProblemEntry) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<WarningProblemEntrySchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<WarningProblemEntry, WarningProblemEntryFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

WarningProblemEntry.dtoValidators = getZodSchemaFromClass(
    WarningProblemEntry,
) as unknown as DtoValidatorTypes<WarningProblemEntrySchemaTypes>;
